import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { MODE_TYPES } from '@aytm/app/stats/page/question-views/psc/lib/constants'
import {
  deltaPole,
  POLE_CENTER,
  POLE_LEFT,
  POLE_RIGHT,
  pointIdOf
} from '@aytm/app/stats/page/question-views/psc/lib/utils'

// Length of each page-level colour theme (see colorThemes in the stats page model);
// batch colour indices cycle modulo this size.
const PALETTE_SIZE = 21

const responsesAppendix = responses => `${formatAmounts(responses)} ${pluralize(responses, 'response', 'responses')}`

const buildColsBase = responses => [
  {
    type: 'title',
    title: 'Attributes',
    appendix: responsesAppendix(responses)
  }
]

// The title is drawn as markup in a flex line, so a pole label that carries markup of its own is a
// separate item from the number before it, and a plain space between two items collapses. A no-break
// space is a character rather than a gap, so `1. Calm` keeps its space.
const titleCell = (idx, text, colorIndex) => {
  return {
    title: `${idx}.&nbsp;${text}`,
    ...(colorIndex != null ? { colorIndex } : {})
  }
}

const progressCell = (item, isLoading) => {
  return {
    amount: item.amount || 0,
    percents: Math.abs(item.percents || 0),
    loading: isLoading
  }
}

// Intensity is one magnitude carrying its response count, not a share split across an n / %
// pair, so it reads as a single value cell instead of a progress column's two slots.
const intensityCell = (item, isLoading) => {
  return {
    value: `${Math.abs(item.percents || 0).toFixed(1)} (${formatAmounts(item.amount || 0)})`,
    loading: isLoading
  }
}

const getCalcParams = ({ mode, answer }) => {
  const statChar = mode.charAt(2) || 'a'
  const titleStat = 'a' === statChar ? 'Mean' : 'Median'
  const dataKey = 'a' === statChar ? 'avg' : 'mean'

  return {
    statChar,
    titleStat,
    dataKey,
    responses: answer?.total || 0,
    isLoading: 'loading' == answer
  }
}

const toggleStatInMode = mode => mode.slice(0, 2) + (mode.endsWith('a') ? 'm' : 'a')

export const calcDiscreteItemsListProps = ({ mode, answer, chartData, percentDecimals = 1 }) => {
  const { responses, isLoading } = getCalcParams({ mode, answer })

  const answers = chartData.map(chart => chart.popularity)

  const cols = [
    ...buildColsBase(responses),
    {
      type: 'progress',
      title: '', // the response count already heads the list as the Attributes appendix
      percentDecimals,
      format: value => formatAmounts(value)
    }
  ]

  const rows = answers.flatMap((a, i) => {
    const idx = i + 1
    const colorIndex = (i % PALETTE_SIZE) + 1
    const group = [
      {
        cells: [titleCell(idx, a[0].text, colorIndex), progressCell(a[0], isLoading)],
        position: 4 * i + 1,
        pointId: pointIdOf(idx, POLE_LEFT)
      },
      {
        cells: [titleCell(idx, a[1].text), progressCell(a[1], isLoading)],
        position: 4 * i + 2,
        pointId: pointIdOf(idx, POLE_CENTER)
      },
      {
        cells: [titleCell(idx, a[2].text, colorIndex), progressCell(a[2], isLoading)],
        position: 4 * i + 3,
        pointId: pointIdOf(idx, POLE_RIGHT)
      },
      ...(i < answers.length - 1 ? [{ type: 'gap', position: 4 * i + 4 }] : [])
    ]
    return group
  })

  return { cols, rows, classNames: ['polarity'] }
}

export const calcPowerDeltaItemsListProps = ({ mode, answer, chartData, onModelChange }) => {
  const { titleStat, dataKey, responses, isLoading } = getCalcParams({ mode, answer })

  const answers = chartData.map(chart => chart.intensity.deltas[dataKey])

  const cols = [
    ...buildColsBase(responses),
    {
      type: 'value',
      title: titleStat,
      onClick: () => onModelChange({ mode: toggleStatInMode(mode) })
    }
  ]

  const rows = answers.flatMap((a, i) => {
    return {
      cells: [titleCell(i + 1, a.text, (i % PALETTE_SIZE) + 1), intensityCell(a, isLoading)],
      position: i + 1,
      // a delta draws one pole only, the side its sign puts it on
      pointId: pointIdOf(i + 1, deltaPole(a.percents))
    }
  })

  return { cols, rows, classNames: ['polarity'] }
}

export const calcPowerBothItemsListProps = ({ mode, answer, chartData, onModelChange }) => {
  const { titleStat, dataKey, responses, isLoading } = getCalcParams({ mode, answer })

  const answers = chartData.map(chart => (chart.intensity.pairs || []).map(p => p[dataKey] || {}))

  const cols = [
    ...buildColsBase(responses),
    {
      type: 'value',
      title: titleStat,
      onClick: () => onModelChange({ mode: toggleStatInMode(mode) })
    }
  ]

  const rows = answers.flatMap((a, i) => {
    const idx = i + 1
    const colorIndex = (i % PALETTE_SIZE) + 1
    return [
      {
        cells: [titleCell(idx, a[0].text, colorIndex), intensityCell(a[0], isLoading)],
        position: 2 * i + 1,
        pointId: pointIdOf(idx, POLE_LEFT)
      },
      {
        cells: [titleCell(idx, a[1].text, colorIndex), intensityCell(a[1], isLoading)],
        position: 2 * i + 2,
        pointId: pointIdOf(idx, POLE_RIGHT)
      }
    ]
  })

  return { cols, rows, classNames: ['polarity'] }
}

export const calcTableProps = ({ mode, answer, chartData, percentDecimals, onModelChange }) => {
  if (mode === MODE_TYPES.d) {
    return calcDiscreteItemsListProps({ mode, answer, chartData, percentDecimals })
  }

  if (mode === MODE_TYPES.pda || mode === MODE_TYPES.pdm) {
    return calcPowerDeltaItemsListProps({ mode, answer, chartData, onModelChange })
  }

  if (mode === MODE_TYPES.pba || mode === MODE_TYPES.pbm) {
    return calcPowerBothItemsListProps({ mode, answer, chartData, onModelChange })
  }

  return { cols: [], rows: [] }
}
